import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import Alert from '@components/custom/Alert';
import Icon from '@components/custom/Icon';
import MenuItem from '@components/custom/MenuItem';
import Toast from '@components/custom/Toast';

import StarMids from '@assets/images/starmids.png';
import FxmbLogo from '@assets/images/fxmb/fxmb-logo.png';
import Mids88Logo from '@assets/images/88mids/88mids-logo.png';
import Snap11Logo from '@assets/images/snap11/snap11-logo.png';

import { menuList as menu } from '@sm/utils/menuList';
import { merchant, whiteLabel } from "@sm/config";

import { loginActions } from '@sm/services/loginActions';

import { hideAlert } from '@sm/store/alertSlice';
import { hideToast } from '@sm/store/toastSlice';
import { REPORT_TYPES } from '@modules/generateReport/GenerateReport';
import { useCheckPageAccess } from '@sm/hooks/useCheckPageAccess';
import { useUnreadNotifications } from '@sm/hooks/useUnreadNotifications';

const Header = (props) => {
  const { userLoggedIn } = props;
  const showAlert = useSelector((state) => state.alert.show);
  const showToast = useSelector((state) => state.toast.show);
  const { wlOtcEnabled, fireblocksEnabled } = useSelector((state) => state.auth);
  const isRollingBalanceEnabled = useCheckPageAccess('REPORTS_CLOSING_BALANCE');

  const hasUnreadNotifications = useUnreadNotifications(userLoggedIn);

  const dispatch = useDispatch();
  const location = useLocation();

  const collapseRef = useRef(null);
  const [isNavOpen, setIsNavOpen] = useState(false);

  const handleToggle = () => {
    setIsNavOpen(prev => !prev);
  };

  const closeMenu = () => {
    setIsNavOpen(false);
  };

  useEffect(() => {
    dispatch(hideAlert());
    dispatch(hideToast());
  }, [location, dispatch]);

  const topBoxes = useMemo(() => [
    {
      title: 'Local Currency',
      items: [{
        label: 'Balance Overview',
        url: '/local-balance'
      },
      {
        label: 'Top-up Payout Balance',
        url: '/top-up-payout-balance'
      },
      ...(isRollingBalanceEnabled ? [{
        label: 'Rolling Balance',
        report: REPORT_TYPES.ROLLING_BALANCE,
        url: `/generate-reports`
      }] : [])]
    }, {
      title: 'Global Currency',
      items: [{
        label: 'Balance Overview',
        url: '/global-balance'
      },
      ...(wlOtcEnabled ? [{
        label: 'Request Withdrawal',
        url: '/request-withdrawal'
      }] : []),
      ...(fireblocksEnabled ? [{
        label: 'Top-up Crypto Wallet',
        url: '/top-up-crypto'
      }] : [])]
    }, {
      title: 'Account Settings',
      items: [{
        label: 'My Wallets',
        url: '/wallets'
      }, {
        label: 'Pricing',
        url: '/pricing'
      }, {
        label: 'User Account',
        url: '/my-account'
      }]
    }
  ], [wlOtcEnabled, fireblocksEnabled, isRollingBalanceEnabled]);

  const logo = (
    <NavLink to='/dashboard' className='navbar-brand'>
      {whiteLabel === 'fxmb' ? (
        <img src={ FxmbLogo } alt="FXMB" style={ { width: '110px', margin: '0 3px 0 50px' } } />
      ) : whiteLabel === "88mids" ? (
        <img src={ Mids88Logo } alt="88mids" style={ { width: '100px' } } />
      ) : whiteLabel === "snap11" ? (
        <img src={ Snap11Logo } alt="Snap11" style={ { width: '130px' } } />
      ) : (
        <>
          <span className="logo-text"> {merchant} BACK OFFICE</span>
          <br/>
          <span className="powered-by"> POWERED BY STAR <img src={ StarMids } className="starmids-logo" alt="StarMids"/> MIDS </span>
        </>
      )}
    </NavLink>
  );

  return (
    <>
      {userLoggedIn && (
        <nav className="navbar navbar-expand-lg shadow">
          <div className="container-fluid">
            { logo }
            <button
              className={ `navbar-toggler ${isNavOpen ? '' : 'collapsed'}` }
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#navbarNav"
              aria-controls="navbarNav"
              aria-expanded={ isNavOpen }
              aria-label="Toggle navigation"
              onClick={ handleToggle }
            >
              <div
                className={ "burger-container " + (isNavOpen ? "open" : "") }
              >
                <div className="burger-bar1"></div>
                <div className="burger-bar2"></div>
                <div className="burger-bar3"></div>
              </div>
            </button>
            <div ref={ collapseRef } className={ `custom-collapse navbar-collapse ${isNavOpen ? 'show' : ''}` } id="navbarNav">
              <ul className="navbar-nav m-auto mb-2 mb-lg-0" onClick={ closeMenu }>
                {menu.map((item, index) => (
                  <MenuItem key={ index } item={ item } />
                ))}
                <MenuItem isLogout handleLogoutClick={ () => loginActions.logout() }/>
              </ul>
            </div>
            <div className='icons d-flex align-items-center'>
              <NavLink to='/my-notifications' className='notifications-link'>
                <Icon name='notifications' />
                {hasUnreadNotifications > 0 && <span className='has-unread'/> }
              </NavLink>
              <div className='d-flex align-items-center nav-item logout'>
                <Icon name='logout' style={ { cursor: 'pointer' } } onClick={ () => loginActions.logout() } />
                <span className='nav-link'>Logout</span>
              </div>
            </div>
          </div>
        </nav>
      )}

      {userLoggedIn && (
        <div className='container top-3-boxes'>
          <div className='row'>
            {topBoxes.map((box, index) => (
              <div key={ index } className='col-sm-12 col-md-4 box-wrap'>
                <div className={ `box box shadow box${index + 1}` }>
                  <p className='title'>{ box.title }</p>
                  <ul>
                    {box.items.map((item, itemIndex) => (
                      <li key={ itemIndex }>
                        <NavLink
                          to={ item.url }
                          className={ ({ isActive }) =>
                            isActive && location?.pathname + location?.search === item.url && location?.state?.report === item?.report
                              ? "active"
                              : ""
                          }
                          { ...(item.report && { state: { report: item.report } } ) }
                        >
                          <Icon name='chevron_right' color='#ffffff' size={ 18 } style={ { marginRight: '2px' } } />
                          {item.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showAlert && (
        <Alert />
      )}

      {showToast && (
        <Toast />
      )}
    </>
  );
};

export default Header;
